import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";

const BOX_SIZES = ["1", "2", "4", "6", "12"];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(r => setS(r.data)).catch(() => {});
  }, []);

  const set = (patch) => setS((cur) => ({ ...cur, ...patch }));
  const setPrice = (k, v) => setS((cur) => ({ ...cur, box_prices: { ...(cur.box_prices || {}), [k]: parseFloat(v) || 0 } }));

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", patch || s);
      setS(data);
      toast.success("Saved");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  if (!s) return <p className="text-sm text-gray-500">Loading...</p>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-settings">
      <div>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl">Settings</h1>
        <p className="text-gray-500 mt-1 text-sm">Control everything on the customer site from here. Changes go live instantly.</p>
      </div>

      <Section title="Order Window">
        <Field label="Order Date Label (hero button)">
          <input className="input-base" value={s.order_date || ""} onChange={e => set({ order_date: e.target.value })} data-testid="settings-order-date" />
        </Field>
        <Field label="Pickup Date Label">
          <input className="input-base" value={s.pickup_date || ""} onChange={e => set({ pickup_date: e.target.value })} data-testid="settings-pickup-date" />
        </Field>
        <Field label="Order Button Text (use {date} placeholder)">
          <input className="input-base" value={s.order_button_text || ""} onChange={e => set({ order_button_text: e.target.value })} placeholder="ORDER NOW FOR {date} PICKUP" data-testid="settings-order-btn-text" />
        </Field>
        <Toggle label="Accept Orders" checked={!!s.orders_open} onChange={(v) => set({ orders_open: v })} testId="settings-orders-open" />
      </Section>

      <Section title="Cookie Pricing">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {BOX_SIZES.map(k => (
            <label key={k} className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">{k}-pack</span>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input type="number" min="0" step="0.01" className="input-base pl-7" value={(s.box_prices && s.box_prices[k]) ?? ""} onChange={e => setPrice(k, e.target.value)} data-testid={`settings-price-${k}`} />
              </div>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Delivery">
        <Toggle label="Enable Delivery" checked={!!s.delivery_enabled} onChange={(v) => set({ delivery_enabled: v })} testId="settings-delivery-enabled" />
        <Toggle label="Enable Pickup" checked={!!s.pickup_enabled} onChange={(v) => set({ pickup_enabled: v })} testId="settings-pickup-enabled" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fee per Mile ($)">
            <input type="number" min="0" step="0.01" className="input-base" value={s.delivery_fee_per_mile ?? 1.5} onChange={e => set({ delivery_fee_per_mile: parseFloat(e.target.value) || 0 })} data-testid="settings-per-mile" />
          </Field>
          <Field label="Delivery Radius (miles)">
            <input type="number" min="0" step="0.1" className="input-base" value={s.delivery_radius_miles ?? 6} onChange={e => set({ delivery_radius_miles: parseFloat(e.target.value) || 0 })} data-testid="settings-radius" />
          </Field>
        </div>
        <Field label="Out-of-zone message (shown to customer)">
          <textarea className="input-base min-h-[80px]" value={s.delivery_zone_message || ""} onChange={e => set({ delivery_zone_message: e.target.value })} data-testid="settings-zone-msg" />
        </Field>
      </Section>

      <Section title="Custom Orders">
        <Toggle label="Accept Custom Orders" checked={!!s.custom_orders_enabled} onChange={(v) => set({ custom_orders_enabled: v })} testId="settings-custom-enabled" />
        <Field label="Minimum notice (days)">
          <input type="number" min="0" className="input-base" value={s.custom_orders_min_notice_days ?? 30} onChange={e => set({ custom_orders_min_notice_days: parseInt(e.target.value) || 0 })} data-testid="settings-custom-min-days" />
        </Field>
      </Section>

      <Section title="Homepage Popup">
        <Toggle label="Show Popup on Homepage" checked={!!s.popup_enabled} onChange={(v) => set({ popup_enabled: v })} testId="settings-popup-enabled" />
        <Field label="Title">
          <input className="input-base" value={s.popup_title || ""} onChange={e => set({ popup_title: e.target.value })} placeholder="ORDER NOW!!" data-testid="settings-popup-title" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Order Deadline">
            <input className="input-base" value={s.popup_order_deadline || ""} onChange={e => set({ popup_order_deadline: e.target.value })} placeholder="June 11th" data-testid="settings-popup-deadline" />
          </Field>
          <Field label="Orders Ready">
            <input className="input-base" value={s.popup_pickup_date || ""} onChange={e => set({ popup_pickup_date: e.target.value })} placeholder="June 14th" data-testid="settings-popup-pickup" />
          </Field>
        </div>
        <Field label="Additional Message">
          <textarea className="input-base min-h-[70px]" value={s.popup_message || ""} onChange={e => set({ popup_message: e.target.value })} placeholder="Limited quantities available." data-testid="settings-popup-msg" />
        </Field>
        <Field label="Show Until (auto-disables after)">
          <input type="datetime-local" className="input-base" value={s.popup_show_until ? s.popup_show_until.slice(0, 16) : ""} onChange={e => set({ popup_show_until: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="settings-popup-until" />
        </Field>
      </Section>

      <div className="sticky bottom-4 z-10 flex gap-3 bg-white border border-gray-100 rounded-2xl p-3 shadow-lg">
        <button onClick={() => save()} disabled={saving} className="btn-pink !py-2 !px-5 text-sm flex-1" data-testid="settings-save-btn">
          {saving ? "SAVING..." : "SAVE ALL CHANGES"}
        </button>
        <button onClick={() => save({ orders_open: !s.orders_open })} className="btn-outline-pink !py-2 !px-5 text-sm" data-testid="settings-toggle-orders">
          {s.orders_open ? "CLOSE ORDERS" : "OPEN ORDERS"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4">
      <h2 className="font-display font-extrabold text-xl">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between bg-[#FFF8F2] p-3 rounded-xl cursor-pointer">
      <span className="font-bold text-sm">{label}</span>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="w-5 h-5 accent-[#FF1F87]" data-testid={testId} />
    </label>
  );
}
